import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Save, Loader2, Search, ChevronDown, ChevronRight,
  Eye, EyeOff, RotateCcw, Download, Upload, Filter, Check, X,
  Globe, Layout, MessageSquare, CreditCard, Users, AlertCircle, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

// Page icons and labels
const PAGE_CONFIG = {
  landing: { icon: Globe, label: 'Landing Page', color: 'bg-orange-100 text-orange-600' },
  footer: { icon: Layout, label: 'Footer', color: 'bg-slate-100 text-slate-600' },
  register: { icon: Users, label: 'Registration', color: 'bg-blue-100 text-blue-600' },
  search: { icon: Search, label: 'Search & Results', color: 'bg-green-100 text-green-600' },
  tradesperson: { icon: Users, label: 'Tradesperson Dashboard', color: 'bg-purple-100 text-purple-600' },
  offer: { icon: MessageSquare, label: 'Offer Form', color: 'bg-yellow-100 text-yellow-700' },
  subscription: { icon: CreditCard, label: 'Subscriptions', color: 'bg-pink-100 text-pink-600' },
  on_call_pro: { icon: AlertCircle, label: 'Priority Boost', color: 'bg-orange-100 text-orange-600' },
  badge: { icon: Check, label: 'Badges & Trust', color: 'bg-emerald-100 text-emerald-600' },
  contact: { icon: MessageSquare, label: 'Contact/Support', color: 'bg-indigo-100 text-indigo-600' },
  waitlist: { icon: Users, label: 'Waitlist', color: 'bg-amber-100 text-amber-600' },
  empty: { icon: AlertCircle, label: 'Empty States', color: 'bg-gray-100 text-gray-600' },
  credits: { icon: CreditCard, label: 'Credits System', color: 'bg-teal-100 text-teal-600' },
  legal: { icon: FileText, label: 'Legal Pages', color: 'bg-slate-100 text-slate-700' },
  notification: { icon: AlertCircle, label: 'Notifications', color: 'bg-blue-100 text-blue-600' },
  onboarding: { icon: Users, label: 'Onboarding', color: 'bg-green-100 text-green-600' },
  marketing: { icon: Globe, label: 'Marketing Blocks', color: 'bg-orange-100 text-orange-600' },
};

// Section labels for better organization
const SECTION_LABELS = {
  hero: 'Hero Section',
  trust: 'Trust Strip',
  free_browsing: 'Free Browsing Banner',
  features: 'Features Section',
  how_it_works: 'How It Works',
  early_access: 'Early Access',
  cta: 'Call to Action',
  regions: 'Region Checker',
  testimonials: 'Testimonials',
  brand: 'Brand Info',
  employer: 'Employer Links',
  trades: 'Tradesperson Links',
  legal: 'Legal Links',
  support: 'Support Links',
  // Additional sections
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  refund: 'Refund Policy',
  cancellation: 'Cancellation Policy',
  cookies: 'Cookie Policy',
  tradesperson: 'Tradesperson Content',
  on_call: 'On Call Features',
};

export default function AdminSiteContent() {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [organizedContent, setOrganizedContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState('landing');
  const [expandedSections, setExpandedSections] = useState({});
  const [editedItems, setEditedItems] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [showOnlyEdited, setShowOnlyEdited] = useState(false);

  // Auth check - admin only
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (response.data.user_type !== 'admin') {
          toast.error('Admin access required');
          navigate('/login');
        }
      } catch (error) {
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-content/admin/all`, {
        withCredentials: true
      });
      setContent(response.data.flat || {});
      setOrganizedContent(response.data.organized || {});
      
      // Expand first section by default
      if (response.data.organized) {
        const pages = Object.keys(response.data.organized);
        if (pages.length > 0) {
          const firstPage = pages[0];
          const sections = Object.keys(response.data.organized[firstPage]?.sections || {});
          if (sections.length > 0) {
            setExpandedSections({ [`${firstPage}.${sections[0]}`]: true });
          }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch content');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value) => {
    setEditedItems(prev => ({
      ...prev,
      [key]: {
        ...content[key],
        value,
        isEdited: true
      }
    }));
  };

  const handleVisibilityToggle = (key) => {
    const currentItem = editedItems[key] || content[key];
    const newVisibility = !currentItem.is_visible;
    
    setEditedItems(prev => ({
      ...prev,
      [key]: {
        ...currentItem,
        is_visible: newVisibility,
        isEdited: true
      }
    }));
  };

  const handleSaveAll = async () => {
    const itemsToSave = Object.entries(editedItems)
      .filter(([_, item]) => item.isEdited)
      .map(([key, item]) => ({
        key,
        value: item.value,
        is_visible: item.is_visible
      }));

    if (itemsToSave.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/site-content/admin/bulk-update`,
        { items: itemsToSave },
        { withCredentials: true }
      );
      toast.success(`Saved ${itemsToSave.length} changes`);
      setEditedItems({});
      fetchContent();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleResetItem = async (key) => {
    if (!window.confirm('Reset this item to its default value?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/site-content/admin/reset/${key}`,
        {},
        { withCredentials: true }
      );
      toast.success('Reset to default');
      // Remove from edited items
      setEditedItems(prev => {
        const newItems = { ...prev };
        delete newItems[key];
        return newItems;
      });
      fetchContent();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-content/admin/export`, {
        withCredentials: true
      });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site-content-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Content exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleImport = async () => {
    try {
      const data = JSON.parse(importJson);
      await axios.post(
        `${BACKEND_URL}/api/site-content/admin/import`,
        data,
        { withCredentials: true }
      );
      toast.success('Content imported');
      setShowImportModal(false);
      setImportJson('');
      fetchContent();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error('Failed to import');
      }
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Get current item value (edited or original)
  const getItemValue = (key) => {
    return editedItems[key]?.value ?? content[key]?.value ?? '';
  };

  const getItemVisibility = (key) => {
    return editedItems[key]?.is_visible ?? content[key]?.is_visible ?? true;
  };

  const isItemEdited = (key) => {
    return editedItems[key]?.isEdited === true;
  };

  // Filter content based on search
  const filteredContent = useMemo(() => {
    if (!organizedContent[selectedPage]) return {};
    
    const pageContent = organizedContent[selectedPage];
    if (!searchQuery && !showOnlyEdited) return pageContent;
    
    const filtered = { sections: {} };
    
    Object.entries(pageContent.sections || {}).forEach(([sectionKey, section]) => {
      const matchingItems = (section.items || []).filter(item => {
        const matchesSearch = !searchQuery || 
          item.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesEditedFilter = !showOnlyEdited || isItemEdited(item.key);
        
        return matchesSearch && matchesEditedFilter;
      });
      
      if (matchingItems.length > 0) {
        filtered.sections[sectionKey] = { items: matchingItems };
      }
    });
    
    return filtered;
  }, [organizedContent, selectedPage, searchQuery, showOnlyEdited, editedItems]);

  const editedCount = Object.values(editedItems).filter(i => i.isEdited).length;
  const pages = Object.keys(organizedContent);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Site Content Manager</h1>
              <p className="text-sm text-slate-500">Edit text content across the platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {editedCount > 0 && (
              <span className="text-sm text-orange-600 font-medium">
                {editedCount} unsaved change{editedCount !== 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={handleExport}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="export-btn"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="import-btn"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || editedCount === 0}
              className="btn-primary flex items-center gap-2"
              data-testid="save-all-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save All Changes
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Page List */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4 sticky top-24">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-500" />
                Pages
              </h2>
              <div className="space-y-1">
                {pages.map((page) => {
                  const config = PAGE_CONFIG[page] || { icon: FileText, label: page, color: 'bg-slate-100 text-slate-600' };
                  const Icon = config.icon;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setSelectedPage(page)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        selectedPage === page
                          ? 'bg-orange-100 text-orange-700'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`page-${page}`}
                    >
                      <div className={`p-1.5 rounded-lg ${config.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="font-medium text-sm">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content - Editor */}
          <div className="col-span-9">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search content by key, value, or description..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    data-testid="search-input"
                  />
                </div>
                <button
                  onClick={() => setShowOnlyEdited(!showOnlyEdited)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    showOnlyEdited
                      ? 'bg-orange-100 border-orange-300 text-orange-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="filter-edited-btn"
                >
                  <Filter className="h-4 w-4" />
                  Edited Only
                </button>
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-4">
              {Object.entries(filteredContent.sections || {}).map(([sectionKey, section]) => {
                const sectionId = `${selectedPage}.${sectionKey}`;
                const isExpanded = expandedSections[sectionId] !== false;
                
                return (
                  <div
                    key={sectionKey}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                    data-testid={`section-${sectionKey}`}
                  >
                    <button
                      onClick={() => toggleSection(sectionId)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        )}
                        <span className="font-semibold text-slate-900">
                          {SECTION_LABELS[sectionKey] || sectionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="text-xs text-slate-400">
                          ({section.items?.length || 0} items)
                        </span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-200 divide-y divide-slate-100">
                        {(section.items || []).map((item) => (
                          <div
                            key={item.key}
                            className={`p-4 ${isItemEdited(item.key) ? 'bg-orange-50' : ''}`}
                            data-testid={`item-${item.key}`}
                          >
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div>
                                <code className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                  {item.key}
                                </code>
                                {item.description && (
                                  <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {item.type && (
                                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                    {item.type}
                                  </span>
                                )}
                                <button
                                  onClick={() => handleVisibilityToggle(item.key)}
                                  className={`p-1.5 rounded transition-colors ${
                                    getItemVisibility(item.key)
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-slate-400 hover:bg-slate-100'
                                  }`}
                                  title={getItemVisibility(item.key) ? 'Visible' : 'Hidden'}
                                >
                                  {getItemVisibility(item.key) ? (
                                    <Eye className="h-4 w-4" />
                                  ) : (
                                    <EyeOff className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleResetItem(item.key)}
                                  className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                  title="Reset to default"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                            {item.type === 'rich_text' || item.type === 'html' ? (
                              <textarea
                                value={getItemValue(item.key)}
                                onChange={(e) => handleValueChange(item.key, e.target.value)}
                                rows={4}
                                className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${
                                  isItemEdited(item.key)
                                    ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                                    : 'border-slate-200 focus:ring-orange-500 focus:border-orange-500'
                                }`}
                              />
                            ) : (
                              <input
                                type="text"
                                value={getItemValue(item.key)}
                                onChange={(e) => handleValueChange(item.key, e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  isItemEdited(item.key)
                                    ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                                    : 'border-slate-200 focus:ring-orange-500 focus:border-orange-500'
                                }`}
                              />
                            )}
                            
                            {isItemEdited(item.key) && (
                              <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Unsaved change
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {Object.keys(filteredContent.sections || {}).length === 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No content items match your search</p>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
              <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Tips
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>Changes are saved instantly across the platform - no deploy needed</li>
                <li>Use the eye icon to hide content items temporarily</li>
                <li>Reset button returns an item to its default value</li>
                <li>Export/Import to backup or transfer content between environments</li>
                <li>Use placeholders like {'{region}'} or {'{min_rate}'} where dynamic values are needed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-500" />
              Import Content
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              Paste the JSON content exported from another environment. This will overwrite existing content.
            </p>
            <textarea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{"content": {...}}'
              rows={10}
              className="w-full font-mono text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="import-json-input"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!importJson.trim()}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="import-confirm-btn"
              >
                <Upload className="h-4 w-4" />
                Import Content
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
